import React, {useEffect, useState} from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  Stack,
  alpha,
  Paper,
  Button,
  Container
} from '@mui/material';
import {
  Add,
  Info,
  MoreVert,
  MoreHoriz,
  DragIndicator,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from '@hello-pangea/dnd';
import CheckIcon from '@mui/icons-material/Check';
import Scrollbar from 'src/minimal-components/scrollbar';
import {useTheme} from '@mui/material/styles';
import {bgBlur} from 'src/theme/css';
import {useBoolean} from 'src/hooks/use-boolean';
import ModuleDetails from './ModuleDetails';
import {usePopover} from '@components/custom-popover';
import {ConfirmDialog} from '@components/custom-dialog';
import AddFileSModule from './AddFileSModule';
import AddLinksModule from './AddLinksModule';
import AddAssignments from './AddAssignments';
import AddContent from './AddContent';
import AddOnlineTest from './AddOnlineTest';
import AddOfflineTest from './AddOfflineTest';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {OnlineContextProvider} from './AdditionData/OnlineTestContext';
import {useLocation, useNavigate} from 'react-router-dom';
import EncryptUtils from 'src/utils/EncryptUtils';
import {
  getLMSResultsData,
  getSearchForm
} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import CreateNewModule from './CreateNewModule';
import {LoadingButton} from '@mui/lab';
import {
  BASE_RECORD_ID,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_UNIQUE_ID,
  BASE_UNIQUE_TYPE,
  LMS_IS_ADMIN,
  LMS_MODULE_ID,
  LMS_TYPE,
  PARAMS_KEY
} from 'src/routes/BaseRoute';
import FormConstants from '@shared/constants/FormConstants';
import Preview from './PreviewModule/Preview';
import TestDetailsPage from './PreviewModule/Preview';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {co} from '@fullcalendar/core/internal-common';
import {useSnackbar} from 'src/minimal-components/snackbar';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';
import {useSettingsContext} from 'src/minimal-components/settings';
import LockedDialog from './LockedDialog';
import UnLockedDialog from './UnLockDialog';
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  module_lock_type_id?: number;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_sub_type_id: number;
    module_link_id: number;
    module_id: string;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: string;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_sub_type_id: number;
      module_link_id: number;
      module_id: string;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
interface FilesAdded {
  id: string;
  name: string;
  duration?: number;
  description?: string;
  attachments?: string[];
  MarkFileAsMandatory?: boolean;
  MarkAsIntroductoryFile?: boolean;
  DisableSeek?: boolean;
  EnableFileDownload?: boolean;
}
interface LinksAdded {
  id: string;
  name: string;
  EmbedURL: string;
  description?: string;
  MarkFileAsMandatory?: boolean;
}
interface AssignmentAdded {
  id: string;
  name: string;
  MaximumMarks: number;
  SubmissionType: number;
  description?: string;
  MarkMandatory?: boolean;
}
interface OfflineTest {
  id: string;
  name: string;
  MaximumMarks: number;
  Duration: number;
  description?: string;
  MarkMandatory?: boolean;
}
interface OnlineTest {
  id: string;
  name: string;
  AllowReattemptsUntil: number;
  Maximumallowedattempts: number;
  description?: string;
  MarkMandatory?: boolean;
}
interface ContentAdded {
  id: string;
  name: string;
  duration?: number | null;
  description?: string;
  MarkMandatory?: boolean;
  content: string;
  MarkAsIntroductoryContent?: boolean;
}
interface ModuleMainProps {
  uiFields: any;
  // eslint-disable-next-line react/boolean-prop-naming
  moduledata: any;
  isDialogopen: boolean;
  fetchData?: boolean;
  handleAddNewRecord: (data: any, module: string) => void;
  handleClick?: () => void;
}
const ModuleMainPage: React.FC<ModuleMainProps> = ({
  uiFields,
  moduledata,
  isDialogopen,
  handleAddNewRecord,
  handleClick,
  fetchData
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const {enqueueSnackbar} = useSnackbar();
  const {
    appState: {applicationName}
  } = useAppContext();
  function useQuery() {
    const {search} = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();
  const lmstype = EncryptUtils.getDecryptedData(location?.search, LMS_TYPE);
  const section = query.get('section');
  const subSection = query.get('subsection');
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const {uiFieldsMain, ModulePreviewData, SetModulePreviewData} =
    useModulePreviewContext();
  const {selectedPreviewFile, setSelectedPreviewFile} =
    useModulePreviewContext();
  const confirm = useBoolean();
  const [addingfile, setAddingFile] = useState(false);
  const [isLockItemsOpen, setLockItemsOpen] = useState(false);
  const [lockitems, setLockItems] = useState<{
    lockData?: {
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
      item_name: string;
      id: string;
      entity_completion_sub_type_id: number;
    }[];
    lockDate?: string;
    chapterTitle: string;
  }>({lockData: [], chapterTitle: ''});
  const [isunLockItemsOpen, setunLockItemsOpen] = useState(false);
  const [unlockitems, setunLockItems] = useState<{
    lockData?: {
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
      item_name: string;
      id: string;
      entity_completion_sub_type_id: number;
    }[];
    lockDate?: string;
    chapterTitle: string;
  }>({lockData: [], chapterTitle: ''});
  const addingLinks = useBoolean();
  const addingAssignments = useBoolean();
  const addingOflineTest = useBoolean();
  const addingOnlineTest = useBoolean();
  const addingFile = useBoolean();
  const addingContents = useBoolean();
  const [isloaded, setIsLoaded] = useState(false);
  const [uiFieldsvals, setUiFields] = useState(uiFields?.searchForm);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [moduleDetails, setModuleDetails] = useState<ModuleData>();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAnchorAdd, setMenuAnchorAdd] = useState<{
    position: null | HTMLElement;
    value: any;
  } | null>(null);
  const [menuAnchorfile, setMenuAnchorFile] = useState<null | HTMLElement>(
    null
  );
  const [previewiId, setPreviewFileId] = useState<{
    fileId: any | null;
    moduleId: any | null;
  } | null>(null);

  const [offlineTestAddded, setofflineTestAddded] = useState<OfflineTest>({
    id: '',
    name: '',
    MaximumMarks: 0,
    Duration: 0,
    description: '',
    MarkMandatory: false
  });
  const [editmodule, setEditModule] = useState(false);
  const [selectedModule, setSelectedModule] = useState<{
    id?: string | null;
    operation?: string | null;
    name?: string;
    Duration?: number;
    Description?: string;
    module?: any;
  }>();
  const [selectedAddModuleId, setSelectedAddModuleid] = useState<{
    id?: string | null;
    operation?: string | null;
    module_id?: number;
    inserted_id?: number;
  }>();
  const [selectedFile, setSelectedFile] = useState<{
    fileId?: string;
    moduleID?: string;
    operation?: string;
    module_id?: string;
    inserted_id?: string;
    type?: string;
    data?: any;
  }>();
  const openDetails = useBoolean();
  const settings = useSettingsContext();
  const moduleinfo = usePopover();
  const onRoleChange = async (
    fileId: string,
    moduleId: string,
    type: string,
    item: any
  ) => {
    settings.onUpdate('themeLayout', 'vertical');
    const paramsKey = EncryptUtils.setEncryptedParams({
      [BASE_RECORD_ID]: uniqueId,
      [LMS_TYPE]: lmstype,
      [BASE_UNIQUE_ID]: fileId,
      [LMS_MODULE_ID]: moduleId,
      [BASE_UNIQUE_TYPE]: type,
      [LMS_IS_ADMIN]: lmsisAdmin
    });
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${FormConstants.MODULE.COURSE_CATALOG}/Preview?${PARAMS_KEY}=${paramsKey}`,
      {
        state: {uiFieldsvals}
      }
    );
  };
  const processModulesData = (data: ModuleData[]) => {
    return data.map(module => ({
      ...module,
      files:
        module.files?.map(file => ({
          ...file,
          locked_linked_items:
            typeof file.locked_linked_items === 'string'
              ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
              : file.locked_linked_items || [], // Keep array or default to empty
          additionalFiles:
            typeof file.additionalFiles === 'string'
              ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
              : file.additionalFiles || [] // Keep array or default to empty
        })) || []
    }));
  };
  const syncExpandedStates = (
    transformed: ModuleData[],
    existing: ModuleData[]
  ) => {
    return transformed.map(module => {
      const matchingModule = existing.find(e => e.id === module.id);

      if (!matchingModule) return module;

      // Sync the `expanded` flag
      const updatedModule = {
        ...module,
        expanded: matchingModule.expanded || false,
        files:
          module?.files &&
          module?.files.map(file => {
            const matchingFile =
              matchingModule?.files &&
              matchingModule.files.find(
                f => f.id === file.id && f.type === file.type
              );
            return {
              ...file,
              fileexpanded: matchingFile?.fileexpanded || false
            };
          })
      };

      return updatedModule;
    });
  };

  const init = async () => {
    try {
      if (uiFieldsvals || uiFields) {
        setIsLoaded(true);
        const {sql_data} =
          uiFieldsvals['module_sql'] || uiFields.searchForm['module_sql'];
        const datavalue = {
          course_overview_id: uniqueId,
          isAdmin: lmsisAdmin === 'True'
        };
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: 'SEARCH'
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };

        // Handling API result
        if (result.data) {
          const transformData = await processModulesData(result.data);
          await SetModulePreviewData(transformData);
        }

        // await handleLegacyAppChange();
        setIsLoaded(false);
      } else {
        const res = await getSearchForm(applicationName, 'module', 'mainPage');
        setUiFields(res.searchForm);
        setIsLoaded(true);
        const {sql_data} = res.searchForm['module_sql'];
        const datavalue = {
          course_overview_id: uniqueId,
          isAdmin: lmsisAdmin === 'True'
        };
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: 'SEARCH'
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };

        // Handling API result
        if (result.data) {
          const transformData = await processModulesData(result.data);
          await SetModulePreviewData(transformData);
        }

        // await handleLegacyAppChange();
        setIsLoaded(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
  };
  // const initfetch = async () => {
  //   try {
  //     if (uiFieldsvals) {
  //       setIsLoaded(true);
  //       const {sql_data} = uiFieldsvals['module_sql'];
  //       const datavalue = {
  //         course_overview_id: uniqueId,
  //         isAdmin: lmsisAdmin === 'True'
  //       };
  //       const data: any = {
  //         sql_data: sql_data,
  //         data: datavalue,
  //         operation: 'SEARCH'
  //       };
  //       const encryptRequestData = EncryptUtils.encryptedData(data);
  //       const result = await getLMSResultsData(applicationName, 'LMS', {
  //         tk: encryptRequestData
  //       });
  //       const processModulesData = (data: ModuleData[]) => {
  //         return data.map(module => ({
  //           ...module,
  //           files:
  //             module.files?.map(file => ({
  //               ...file,
  //               locked_linked_items:
  //                 typeof file.locked_linked_items === 'string'
  //                   ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
  //                   : file.locked_linked_items || [], // Keep array or default to empty
  //               additionalFiles:
  //                 typeof file.additionalFiles === 'string'
  //                   ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
  //                   : file.additionalFiles || [] // Keep array or default to empty
  //             })) || []
  //         }));
  //       };

  //       // Handling API result
  //       if (result.data) {
  //         const transformData = await processModulesData(result.data);
  //         await SetModulePreviewData(transformData);
  //       }

  //       // await handleLegacyAppChange();
  //       setIsLoaded(false);
  //     } else {
  //       const res = await getSearchForm(applicationName, 'module');
  //       setUiFields(res.searchForm);
  //       setIsLoaded(true);
  //       const {sql_data} = res.searchForm['module_sql'];
  //       const datavalue = {
  //         course_overview_id: uniqueId,
  //         isAdmin: lmsisAdmin === 'True'
  //       };
  //       const data: any = {
  //         sql_data: sql_data,
  //         data: datavalue,
  //         operation: 'SEARCH'
  //       };
  //       const encryptRequestData = EncryptUtils.encryptedData(data);
  //       const result = await getLMSResultsData(applicationName, 'LMS', {
  //         tk: encryptRequestData
  //       });
  //       const processModulesData = (data: ModuleData[]) => {
  //         return data.map(module => ({
  //           ...module,
  //           files:
  //             module.files?.map(file => ({
  //               ...file,
  //               locked_linked_items:
  //                 typeof file.locked_linked_items === 'string'
  //                   ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
  //                   : file.locked_linked_items || [], // Keep array or default to empty
  //               additionalFiles:
  //                 typeof file.additionalFiles === 'string'
  //                   ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
  //                   : file.additionalFiles || [] // Keep array or default to empty
  //             })) || []
  //         }));
  //       };

  //       // Handling API result
  //       if (result.data) {
  //         const transformData = await processModulesData(result.data);
  //         await SetModulePreviewData(transformData);
  //       }

  //       // await handleLegacyAppChange();
  //       setIsLoaded(false);
  //     }
  //   } catch (e) {
  //     const errMessage = getErrorMessage(e);
  //     enqueueSnackbar(errMessage, SnackBarConfig.getError());
  //     setIsLoaded(false);
  //   }
  // };
  // useEffect(() => {
  //   const fetchData = async () => {
  //     initfetch();
  //   };

  //   fetchData();
  // }, []);

  const deleted = async () => {
    try {
      if (selectedModule?.id && uiFieldsvals) {
        setDeleteLoading(true);

        const {sql_data} = uiFieldsvals['module_sql'];
        // console.log('selectedModule', selectedModule);
        const datavalue = {
          course_overview_id: uniqueId,
          module_id: selectedModule?.id,
          isAdmin: lmsisAdmin === 'True'
        };
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: 'DELETE'
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        // const processModulesData = (data: ModuleData[]) => {
        //   return data.map(module => ({
        //     ...module,
        //     files:
        //       module.files?.map(file => ({
        //         ...file,
        //         additionalFiles:
        //           typeof file.additionalFiles === 'string'
        //             ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
        //             : file.additionalFiles || [] // Keep array or default to empty
        //       })) || []
        //   }));
        // };

        // // Handling API result
        // if (result.data) {
        //   SetModulePreviewData(processModulesData(result.data));
        // }
        if (result.data[1]) {
          const transformData = await processModulesData(result.data[1]);
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
        // await handleLegacyAppChange();
        setDeleteLoading(false);
        confirm.onFalse();
        enqueueSnackbar(result.message, SnackBarConfig.getSuccess());
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());

      setDeleteLoading(false);
      confirm.onFalse();
    }
  };
  const deletedfiles = async () => {
    try {
      if (selectedFile && uiFieldsvals) {
        setDeleteLoading(true);

        const {sql_data} = uiFieldsvals['module_sql'];
        const datavalue = {
          course_overview_id: uniqueId,
          file_id: selectedFile.type === 'file' ? selectedFile?.fileId : null,
          link_id: selectedFile.type === 'link' ? selectedFile?.fileId : null,
          content_id:
            selectedFile.type === 'content' ? selectedFile?.fileId : null,
          assignment_id:
            selectedFile.type === 'assignment' ? selectedFile?.fileId : null,
          online_test_id:
            selectedFile.type === 'onlinetest' ? selectedFile?.fileId : null,
          isAdmin: lmsisAdmin === 'True'
        };
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: 'DELETE'
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        // const processModulesData = (data: ModuleData[]) => {
        //   return data.map(module => ({
        //     ...module,
        //     files:
        //       module.files?.map(file => ({
        //         ...file,
        //         additionalFiles:
        //           typeof file.additionalFiles === 'string'
        //             ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
        //             : file.additionalFiles || [] // Keep array or default to empty
        //       })) || []
        //   }));
        // };

        // // Handling API result
        // if (result.data) {
        //   SetModulePreviewData(processModulesData(result.data));
        // }
        if (result.data[1]) {
          const transformData = await processModulesData(result.data[1]);
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
        // await handleLegacyAppChange();
        setDeleteLoading(false);
        confirm.onFalse();
        enqueueSnackbar(result.message, SnackBarConfig.getSuccess());
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setDeleteLoading(false);
      confirm.onFalse();
    }
  };
  useEffect(() => {
    const fetchDataGet = async () => {
      if (uiFieldsvals === undefined) {
        // const res = await getSearchForm(applicationName, 'module', 'mainPage');
        setUiFields(uiFields);
      }
      if (ModulePreviewData.length === 0 || ModulePreviewData === undefined) {
        await init();
      }
    };
    fetchDataGet();
  }, [ModulePreviewData]);
  // useEffect(() => {
  //   const fetchDataGet = async () => {
  //     if (!isDialogopen && fetchData) {
  //       await init();
  //       handleClick && handleClick();
  //     }
  //   };
  //   fetchDataGet();
  // }, [isDialogopen, fetchData]);
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    moduleId: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedModule({id: moduleId});
  };
  const handleAddOpen = (
    event: React.MouseEvent<HTMLElement>,
    moduleId: string
  ) => {
    setMenuAnchorAdd({position: event.currentTarget, value: moduleId});
  };
  const handleFileMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    fileId: string,
    moduleId: string,
    filetype: string,
    file: any
  ) => {
    setMenuAnchorFile(event.currentTarget);
    setSelectedFile({
      fileId: fileId,
      moduleID: moduleId,
      type: filetype,
      data: file
    });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuAnchorFile(null);
    setSelectedModule(undefined);
    setSelectedFile(undefined);
  };
  const handleMenuAddClose = () => {
    setMenuAnchorAdd(null);
    setSelectedAddModuleid(undefined);
  };
  const handleSelectionFile = (
    event: any,
    id: string,
    moduleID: string,
    operation: string,
    type?: string,
    file?: any
  ) => {
    setMenuAnchorFile(null);
    setSelectedFile({
      fileId: id,
      moduleID: moduleID,
      operation: operation,
      module_id:  moduleID,
      inserted_id: id,
      type: type,
      data: file
    });
  };
  const handleSelectionAdd = (event: any, id: string) => {
    setMenuAnchorAdd(null);
    setSelectedFile(undefined);
    setSelectedAddModuleid({id: id, operation: 'INSERT'});
  };
  const handleSelectionModule = (
    event: any,
    id: string,
    operation: string,
    name?: string,
    duration?: any,
    description?: any,
    module?: any
  ) => {
    setMenuAnchor(null);
    setSelectedModule({
      id: id,
      operation,
      name,
      Duration: duration,
      Description: description,
      module: module
    });
  };
  const toggleModule = (moduleId: string) => {
    SetModulePreviewData((prevModules: any[]) =>
      prevModules.map(
        (module: {id: string; files: string | any[]; expanded: any}) => {
          if (module.id === moduleId) {
            if (module?.files) {
              return module?.files.length > 0
                ? {...module, expanded: !module.expanded}
                : {...module, expanded: false}; // Explicitly set expanded to false if there are no files
            }
          }
          return module;
        }
      )
    );
  };
  const handlePreview = (
    fileId: string,
    moduleId: string,
    type: string,
    previewData: any
  ) => {
    setPreviewFileId({fileId:  fileId, moduleId: moduleId});
    SetModulePreviewData((prevState: any[]) =>
      prevState.map(module => ({
        ...module,
        files: module.files?.map(
          (file: {id: string; fileexpanded: any; type: string}) => ({
            ...file,
            fileexpanded:
              module.id === moduleId && file.id === fileId && file.type === type
                ? !file.fileexpanded // Toggle the current file
                : false // Collapse all other files
          })
        )
      }))
    );
    setSelectedPreviewFile({...previewData, fileexpanded: true});
    onRoleChange(fileId, moduleId, type, 'preview');
  };
  const onCourseComplete = async (
    moduleId: string,
    fileId: string,
    previewData: boolean,
    type: string
  ) => {
    if (uiFieldsvals) {
      const {sql_data} = uiFieldsvals['module_sql'];
      //console.log('selectedFile', selectedFile);
      const datavalue = {
        course_overview_id: uniqueId,
        file_id: fileId,
        module_id: moduleId,
        coursecomplete: previewData,
        isAdmin: lmsisAdmin === 'True',
        name: type
      };
      const data: any = {
        sql_data: sql_data,
        data: datavalue,
        operation: 'UPDATE'
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getLMSResultsData(applicationName, 'LMS', {
        tk: encryptRequestData
      });

      // Handling API result
      if (result.data) {
        const transformData = await processModulesData(result.data);
        const updatedTransformed = syncExpandedStates(
          transformData,
          ModulePreviewData
        );
        await SetModulePreviewData(updatedTransformed);
      }
    }
  };
  const updateCompletionStatus = () => {
    const updatedModules = ModulePreviewData.map(module => {
      // If no locked items, skip
      if (
        !module.locked_linked_items ||
        module.locked_linked_items.length === 0
      ) {
        return module;
      }

      const updatedFiles = (module.files ?? []).map(file => {
        // Check if file is linked by any locked_linked_item
        const match =
          module?.locked_linked_items &&
          module?.locked_linked_items.find(
            linked =>
              linked?.entity_completion_sub_type_id &&
              linked?.entity_completion_sub_type_id === file.id &&
              linked.module_id === file.module_id &&
              linked.type === file.type
          );

        if (match && file.ChildItems_Completed) {
          return {
            ...file,
            is_completed: 1
          };
        }

        return file;
      });

      return {
        ...module,
        files: updatedFiles
      };
    });

    SetModulePreviewData(updatedModules);
  };
  const handleCompleted = (
    fileId: string,
    moduleId: string,
    type: string,
    previewData?: boolean
  ) => {
    SetModulePreviewData((prevState: ModuleData[]) =>
      prevState.map(module => {
        const updatedFiles =
          module.files?.map(file => ({
            ...file,
            ChildItems_Completed:
              file.id === fileId && module.id === moduleId && file.type === type
                ? !previewData // Toggle only the specific file
                : file?.ChildItems_Completed
                  ? file?.ChildItems_Completed
                  : false
          })) || [];

        // Check if all ChildItems_Completed are true
        const allFilesCompleted =
          updatedFiles.length > 0 &&
          updatedFiles.every(file => file.ChildItems_Completed);

        return {
          ...module,
          files: updatedFiles,
          Module_Completed: allFilesCompleted // Update module completion status
        };
      })
    );
    onCourseComplete(moduleId, fileId, !previewData, type);
  };
  const handleSequenceEdit = async (movedValue: any, oldModuleId?: number) => {
    try {
      const operations = 'UPDATESEQUENCE';
      // if (operation === 'UPDATE') {
      //   datavalue.id = onlineData.inserted_id;
      // }
      if (uiFieldsvals) {
        // console.log('sql_data', movedValue, uiFieldsvals);
        const {sql_data} = uiFieldsvals['module_sql'];
        // console.log('sql_data', sql_data, movedValue, uiFieldsvals);
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        // setQuestionLoader(true);
        const data: any = {
          sql_data: sql_data,
          data: {
            module_id: oldModuleId ? oldModuleId : movedValue.module_id,
            child_table_id: movedValue.id,
            new_sequence: movedValue.sequence,
            new_module_id: movedValue.module_id,
            course_overview_id: movedValue.course_overview_id || uniqueId
          },
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        // console.log('results', result);
        // setSaved(true);
        // setQuestionData(result.data[0]);
        // setQuestionLoader(false);
        // if (operation === 'INSERT') {
        //   datavalue.id = result.data[0].id; // Assign the inserted ID from the result
        //   datavalue.sequence = result.data[0].sequence; // Assign the inserted sequence from the result
        //   return datavalue;
        // } else {
        //   return true;
        // }
        // Send data to the server
        enqueueSnackbar(result.message, SnackBarConfig.getSuccess());
      }
    } catch (e) {
      // console.log('ee', e);
      // setQuestionLoader(false);
      if (e instanceof Error) {
        enqueueSnackbar(e.message, SnackBarConfig.getError());
      }
    }
  };
  const handleSequenceModuleEdit = async (
    movedValue: any,
    oldModuleId?: number
  ) => {
    try {
      const operations = 'MODULESEQUENCE';
      // if (operation === 'UPDATE') {
      //   datavalue.id = onlineData.inserted_id;
      // }
      //console.log('movedValue.sequence', movedValue.sequence);
      if (uiFieldsvals) {
        // console.log('sql_data', movedValue, uiFieldsvals);
        const {sql_data} = uiFieldsvals['module_sql'];
        // console.log('sql_data', sql_data, movedValue, uiFieldsvals);
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        // setQuestionLoader(true);
        const data: any = {
          sql_data: sql_data,
          data: {
            module_id: movedValue.id,
            new_sequence: movedValue.sequence,
            course_overview_id: movedValue.course_overview_id
          },
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        // console.log('results', result);
        // setSaved(true);
        // setQuestionData(result.data[0]);
        // setQuestionLoader(false);
        // if (operation === 'INSERT') {
        //   datavalue.id = result.data[0].id; // Assign the inserted ID from the result
        //   datavalue.sequence = result.data[0].sequence; // Assign the inserted sequence from the result
        //   return datavalue;
        // } else {
        //   return true;
        // }
        // Send data to the server
        enqueueSnackbar(result.message, SnackBarConfig.getSuccess());
      }
    } catch (e) {
      // console.log('ee', e);
      // setQuestionLoader(false);
      if (e instanceof Error) {
        enqueueSnackbar(e.message, SnackBarConfig.getError());
      }
    }
  };
  const onDragEnd = async ({destination, source, type}: DropResult) => {
    if (!destination) return; // Exit if dropped outside
    const updatedModules = [...ModulePreviewData];

    if (type === 'module') {
      // Reorder ModulePreviewData
      const [movedModule] = updatedModules.splice(source.index, 1);
      updatedModules.splice(destination.index, 0, movedModule);

      // ✅ Update the sequence based on new order
      updatedModules.forEach((module, index) => {
        module.sequence = index + 1; // Assign new sequence value
      });
      handleSequenceModuleEdit(movedModule);
    } else if (type === 'file') {
      const sourceModuleIndex = updatedModules.findIndex(
        m => String(m.id) === source.droppableId
      );
      const destModuleIndex = updatedModules.findIndex(
        m => String(m.id) === destination.droppableId
      );

      // ✅ Ensure source and destination ModulePreviewData exist
      if (sourceModuleIndex === -1 || destModuleIndex === -1) {
        console.error('Error: Source or destination module not found');
        return;
      }

      const sourceFiles = [...(updatedModules[sourceModuleIndex].files ?? [])];
      const destFiles = [...(updatedModules[destModuleIndex].files ?? [])];

      if (source.index < 0 || source.index >= sourceFiles.length) {
        console.error('Error: Invalid source index', source.index);
        return;
      }

      const [movedFile] = sourceFiles.splice(source.index, 1);

      if (source.droppableId === destination.droppableId) {
        // ✅ Reorder within the same module

        sourceFiles.splice(destination.index, 0, movedFile);
        updatedModules[sourceModuleIndex].files = sourceFiles;

        // ✅ Update file sequence within the module
        sourceFiles.forEach((file, index) => {
          file.sequence = index + 1; // Assign new sequence value
        });
        handleSequenceEdit(movedFile);
      } else {
        // ✅ Move file to another module

        const module_id_old = movedFile.module_id;
        movedFile.module_id = updatedModules[destModuleIndex].id;
        destFiles.splice(destination.index, 0, movedFile);
        updatedModules[sourceModuleIndex].files = sourceFiles;
        updatedModules[destModuleIndex].files = destFiles;

        // Expand destination module
        updatedModules[destModuleIndex].expanded = true;
        // ✅ Update file sequence in both source and destination ModulePreviewData
        sourceFiles.forEach((file, index) => {
          file.sequence = index + 1; // Assign new sequence value
        });

        destFiles.forEach((file, index) => {
          file.sequence = index + 1; // Assign new sequence value
        });
        handleSequenceEdit(movedFile, module_id_old);
      }

      // Collapse source module if no files are left
      if (sourceFiles.length === 0) {
        updatedModules[sourceModuleIndex].expanded = false;
      }
    }
    // ✅ Update state with re-ordered ModulePreviewData/files
    SetModulePreviewData(updatedModules);
  };

  const onDelete = async () => {
    if (selectedModule) {
      // Delete the selected module
      await deleted();
      // SetModulePreviewData((prevModules: any[]) =>
      //   prevModules.filter(
      //     (module: {id: string | null | undefined}) =>
      //       module.id !== selectedModule.id
      //   )
      // );
      setSelectedModule(undefined);
      // Clear selection after deletion
    }
    if (selectedFile?.moduleID && selectedFile?.fileId) {
      // Delete the selected file from the corresponding module
      await deletedfiles();
      // SetModulePreviewData((prevModules: any[]) =>
      //   prevModules.map((module: {id: string | undefined; files: any[]}) =>
      //     module.id === selectedFile.moduleID
      //       ? {
      //           ...module,
      //           files: module?.files?.filter(
      //             (file: {id: string | undefined}) =>
      //               file.id !== selectedFile.fileId
      //           )
      //           // expanded:
      //           //   module?.files?.filter(
      //           //     file => file?.id !== selectedFile?.fileId
      //           //   ).length > 0
      //         }
      //       : module
      //   )
      // );
      setSelectedFile(undefined); // Clear selection after deletion
    }
  };
  // if (section === 'module' && subSection === 'preview') {
  //   return <TestDetailsPage uiFields={uiFieldsvals} />;
  // }
  return isloaded ? (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'50vh'}>
      <LoadingScreen />
    </Stack>
  ) : ModulePreviewData !== null &&
    ModulePreviewData?.length > 0 &&
    ModulePreviewData[0].id !== null ? (
    <>
      <Box sx={{p: 3}}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="all-ModulePreviewData"
            direction="vertical"
            type="module">
            {(provided: any) => (
              <Stack
                ref={provided.innerRef}
                {...provided.droppableProps}
                spacing={3}
                direction="row"
                alignItems="flex-start"
                sx={{
                  display: 'grid',
                  p: 0.25,
                  height: 1
                }}>
                {ModulePreviewData.sort((a, b) => a.sequence - b.sequence).map(
                  (module, moduleIndex) => (
                    <Draggable
                      key={`${module.id}_${moduleIndex}_module`}
                      draggableId={String(module.id)}
                      index={moduleIndex}>
                      {(provided, snapshot) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            px: 2,
                            borderRadius: 2,
                            bgcolor: 'background.neutral',
                            ...(snapshot.isDragging && {
                              bgcolor: theme =>
                                alpha(theme.palette.grey[500], 0.24)
                            })
                          }}>
                          <Droppable
                            droppableId={String(module.id)}
                            type="file">
                            {dropProvided => (
                              <Stack
                                ref={dropProvided.innerRef}
                                {...dropProvided.droppableProps}
                                spacing={2}>
                                <div>
                                  <ListItem
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: '#fff',
                                      p: 1
                                    }}>
                                    {/* Drag Handle */}
                                    {lmstype !== 'view' && (
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{cursor: 'move !important', mr: 2}}>
                                        <DragIndicator sx={{fill: '#68747f'}} />
                                      </Box>
                                    )}
                                    {/* Expand/Collapse Icon */}
                                    <IconButton
                                      onClick={() => toggleModule(module.id)}
                                      disabled={
                                        lmsisAdmin === 'False'
                                          ? module?.module_lock_type_id === 3
                                            ? module?.files?.length === 0
                                              ? true
                                              : module?.module_lock_type_id ===
                                                    3 && module?.is_completed
                                                ? false
                                                : module?.module_lock_type_id ===
                                                      3 &&
                                                    module?.locked_linked_items &&
                                                    module?.locked_linked_items
                                                      ?.length > 0 &&
                                                    module?.is_completed !==
                                                      false
                                                  ? true
                                                  : false
                                            : module?.module_lock_type_id ===
                                                  2 && module?.IsUnlocked
                                              ? false
                                              : false
                                          : false
                                      }
                                      size="small">
                                      {module.expanded ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </IconButton>

                                    {/* Module Name */}
                                    <Typography sx={{flex: 1, ml: 1}}>
                                      {module.name}
                                    </Typography>

                                    {/* Action Icons */}
                                    {module.module_lock_type_id !== 1 &&
                                    lmstype === 'view' &&
                                    lmsisAdmin === 'False' ? (
                                      (module?.IsUnlocked &&
                                        module.module_lock_type_id === 2) ||
                                      (module?.is_completed &&
                                        module.module_lock_type_id === 3) ? (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setunLockItemsOpen(true);
                                            if (
                                              module.locked_linked_items &&
                                              module.module_lock_type_id === 3
                                            ) {
                                              setunLockItems({
                                                lockData:
                                                  module.locked_linked_items,
                                                chapterTitle: module.name
                                              });
                                            }
                                            if (
                                              module.locked_until_date &&
                                              module.module_lock_type_id === 2
                                            ) {
                                              setunLockItems({
                                                lockDate:
                                                  module.locked_until_date,
                                                chapterTitle: module.name
                                              });
                                            }
                                          }}>
                                          <LockOpenIcon />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setLockItemsOpen(true);
                                            if (
                                              module.locked_linked_items &&
                                              module.module_lock_type_id === 3
                                            ) {
                                              setLockItems({
                                                lockData:
                                                  module.locked_linked_items,
                                                chapterTitle: module.name
                                              });
                                            }
                                            if (
                                              module.locked_until_date &&
                                              module.module_lock_type_id === 2
                                            ) {
                                              setLockItems({
                                                lockDate:
                                                  module.locked_until_date,
                                                chapterTitle: module.name
                                              });
                                            }
                                          }}>
                                          <LockIcon />
                                        </IconButton>
                                      )
                                    ) : (
                                      module.module_lock_type_id !== 1 && (
                                        <IconButton
                                          size="small"
                                          onClick={event => {
                                            setLockItemsOpen(true);
                                            if (
                                              module.locked_linked_items &&
                                              module.module_lock_type_id === 3
                                            ) {
                                              setLockItems({
                                                lockData:
                                                  module.locked_linked_items,
                                                chapterTitle: module.name
                                              });
                                            }
                                            if (
                                              module.locked_until_date &&
                                              module.module_lock_type_id === 2
                                            ) {
                                              setLockItems({
                                                lockDate:
                                                  module.locked_until_date,
                                                chapterTitle: module.name
                                              });
                                            }
                                          }}>
                                          <LockIcon />
                                        </IconButton>
                                      )
                                    )}

                                    {lmstype !== 'view' && (
                                      <IconButton
                                        size="small"
                                        onClick={event => {
                                          handleAddOpen(event, module.id);
                                        }}>
                                        <Add />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      onClick={e => {
                                        openDetails.onTrue();
                                        setModuleDetails(module);
                                      }}
                                      size="small">
                                      <Info />
                                    </IconButton>
                                    {lmstype === 'view' &&
                                      lmsisAdmin === 'False' && (
                                        <IconButton
                                          sx={{
                                            width: 25,
                                            height: 25,
                                            borderRadius: '50%',
                                            backgroundColor:
                                              module.Module_Completed ||
                                              (module.files &&
                                                module.files.length === 0)
                                                ? 'green'
                                                : '#b3beb3',
                                            border:
                                              module.Module_Completed ||
                                              (module.files &&
                                                module.files.length === 0)
                                                ? 'none'
                                                : '2px solid gray',
                                            color:
                                              module.Module_Completed ||
                                              (module.files &&
                                                module.files.length === 0)
                                                ? 'white'
                                                : 'gray',
                                            transition: 'all 0.3s',
                                            '&:hover': {
                                              backgroundColor:
                                                module.Module_Completed ||
                                                (module.files &&
                                                  module.files.length === 0)
                                                  ? 'darkgreen'
                                                  : 'rgba(0, 0, 0, 0.1)'
                                            }
                                          }}>
                                          {<CheckIcon />}
                                        </IconButton>
                                      )}
                                    {lmstype !== 'view' && (
                                      <IconButton
                                        size="small"
                                        onClick={event => {
                                          handleMenuOpen(event, module.id);
                                        }}>
                                        <MoreVert />
                                      </IconButton>
                                    )}

                                    {/* Dropdown Menu */}
                                    <Menu
                                      anchorEl={menuAnchor}
                                      open={
                                        Boolean(menuAnchor) &&
                                        selectedModule !== undefined &&
                                        selectedModule.id === module.id
                                      }
                                      onClose={handleMenuClose}>
                                      <MenuItem
                                        onClick={e => {
                                          setEditModule(true);
                                          handleSelectionModule(
                                            e,
                                            module.id,
                                            'UPDATE',
                                            module.name,
                                            module.duration,
                                            module.description,
                                            module
                                          );
                                        }}>
                                        Edit
                                      </MenuItem>
                                      <MenuItem
                                        onClick={e => {
                                          confirm.onTrue();
                                          handleSelectionModule(
                                            e,
                                            module.id,
                                            'DELETE'
                                          );
                                        }}>
                                        Delete
                                      </MenuItem>
                                    </Menu>
                                    <Menu
                                      anchorEl={menuAnchorAdd?.position}
                                      open={Boolean(menuAnchorAdd?.position)}
                                      onClose={handleMenuAddClose}>
                                      <MenuItem
                                        onClick={e => {
                                          addingFile.onTrue();
                                          handleSelectionAdd(
                                            e,
                                            menuAnchorAdd?.value
                                          );
                                        }}>
                                        File
                                      </MenuItem>
                                      <MenuItem
                                        onClick={e => {
                                          addingLinks.onTrue();
                                          handleSelectionAdd(
                                            e,
                                            menuAnchorAdd?.value
                                          );
                                        }}>
                                        Link
                                      </MenuItem>
                                      {/* <MenuItem
                                        onClick={e => {
                                          addingAssignments.onTrue();
                                          handleSelectionAdd(
                                            e,
                                            menuAnchorAdd?.value
                                          );
                                        }}>
                                        Assignment
                                      </MenuItem> */}
                                      {/* <MenuItem
                                        onClick={e => {
                                          addingOflineTest.onTrue();
                                          handleSelectionAdd(e, module.id);
                                        }}>
                                        Offline Test
                                      </MenuItem> */}
                                      <MenuItem
                                        onClick={e => {
                                          addingOnlineTest.onTrue();
                                          handleSelectionAdd(
                                            e,
                                            menuAnchorAdd?.value
                                          );
                                        }}>
                                        Online Test
                                      </MenuItem>
                                      <MenuItem
                                        onClick={e => {
                                          addingContents.onTrue();
                                          handleSelectionAdd(
                                            e,
                                            menuAnchorAdd?.value
                                          );
                                        }}>
                                        Content
                                      </MenuItem>
                                    </Menu>
                                  </ListItem>

                                  {/* Expandable Files */}
                                  <Collapse in={module.expanded}>
                                    <Droppable
                                      droppableId={String(module.id)}
                                      type="file">
                                      {dropProvided => (
                                        <Stack
                                          ref={dropProvided.innerRef}
                                          {...dropProvided.droppableProps}
                                          spacing={2.5}
                                          sx={{
                                            py: 1
                                          }}>
                                          {module?.files &&
                                            module?.files
                                              .sort(
                                                (a, b) =>
                                                  a.sequence - b.sequence
                                              )
                                              .map((file, fileIndex) => (
                                                <Draggable
                                                  key={`${file.type}-${String(file.id)}`}
                                                  draggableId={`${file.type}-${String(file.id)}`}
                                                  index={fileIndex}>
                                                  {(
                                                    provided: any,
                                                    snapshot: any
                                                  ) => (
                                                    <Paper
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      //   onClick={openDetails.onTrue}
                                                      sx={{
                                                        width: 1,
                                                        // borderRadius: 1.5,
                                                        alignItems: 'center',
                                                        justifyContent:
                                                          'space-between',
                                                        overflow: 'hidden',
                                                        position: 'relative',
                                                        bgcolor:
                                                          'background.default',
                                                        boxShadow:
                                                          theme.customShadows
                                                            .z1,
                                                        '&:hover': {
                                                          boxShadow:
                                                            theme.customShadows
                                                              .z20,
                                                          '& .drag-indicator': {
                                                            fill: '#68747f' // Change this to your desired color
                                                          },
                                                          '& .mhoriz-indicator':
                                                            {
                                                              fill: '#68747f' // Change this to your desired color
                                                            }
                                                        },
                                                        borderRadius: 0,
                                                        ...(snapshot.isDragging && {
                                                          boxShadow:
                                                            theme.customShadows
                                                              .z20,
                                                          ...bgBlur({
                                                            opacity: 0.48,
                                                            color:
                                                              theme.palette
                                                                .background
                                                                .default
                                                          })
                                                        })
                                                      }}>
                                                      <Box
                                                        sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          borderLeft:
                                                            '2px solid #ddd', // Visual separation
                                                          justifyContent:
                                                            'space-between'
                                                        }}>
                                                        <Box
                                                          sx={{
                                                            display: 'flex',
                                                            flex: 1
                                                          }}>
                                                          {lmstype !==
                                                          'view' ? (
                                                            <Box
                                                              {...provided.dragHandleProps}
                                                              sx={{
                                                                cursor:
                                                                  'move !important',
                                                                mr: 2,
                                                                ml: 1,
                                                                display: 'flex'
                                                              }}>
                                                              <DragIndicator
                                                                className="drag-indicator" // Add this class to target the color on hover
                                                                sx={{
                                                                  fill: '#F4F6F8', // Default color
                                                                  transition:
                                                                    'fill 0.3s ease-in-out'
                                                                }}
                                                              />
                                                            </Box>
                                                          ) : (
                                                            <Box
                                                              sx={{
                                                                mr: 5.5
                                                              }}></Box>
                                                          )}
                                                          <Typography
                                                            onClick={() => {
                                                              handlePreview(
                                                                file.id,
                                                                module.id,
                                                                file.type,
                                                                file
                                                              );
                                                              handleSelectionFile(
                                                                'Preview',
                                                                file.id,
                                                                module.id,
                                                                'UPDATE'
                                                              );
                                                            }}
                                                            sx={{
                                                              cursor: 'pointer',
                                                              flex: 1
                                                            }}
                                                            fontSize="1rem !important">
                                                            {file.name}
                                                          </Typography>
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              mx: 2,
                                                              display: 'flex',
                                                              alignItems:
                                                                'center'
                                                            }}>
                                                            {file?.time}
                                                          </Typography>
                                                        </Box>
                                                        <Box>
                                                          {lmstype === 'view' &&
                                                          lmsisAdmin ===
                                                            'False' &&
                                                          file.lock_type_id !==
                                                            1 ? (
                                                            (file?.IsUnlocked &&
                                                              file.lock_type_id ===
                                                                2) ||
                                                            (file?.is_completed &&
                                                              file.lock_type_id ===
                                                                3) ? (
                                                              <IconButton
                                                                size="small"
                                                                onClick={event => {
                                                                  setunLockItemsOpen(
                                                                    true
                                                                  );
                                                                  if (
                                                                    file.locked_linked_items &&
                                                                    file.lock_type_id ===
                                                                      3
                                                                  ) {
                                                                    setunLockItems(
                                                                      {
                                                                        lockData:
                                                                          file.locked_linked_items,
                                                                        chapterTitle:
                                                                          file.name
                                                                      }
                                                                    );
                                                                  }
                                                                  if (
                                                                    file.locked_until_date &&
                                                                    file.lock_type_id ===
                                                                      2
                                                                  ) {
                                                                    setunLockItems(
                                                                      {
                                                                        lockDate:
                                                                          file.locked_until_date,
                                                                        chapterTitle:
                                                                          file.name
                                                                      }
                                                                    );
                                                                  }
                                                                }}>
                                                                <LockOpenIcon />
                                                              </IconButton>
                                                            ) : (
                                                              <IconButton
                                                                size="small"
                                                                onClick={event => {
                                                                  setLockItemsOpen(
                                                                    true
                                                                  );
                                                                  if (
                                                                    file.locked_linked_items &&
                                                                    file.lock_type_id ===
                                                                      3
                                                                  ) {
                                                                    setLockItems(
                                                                      {
                                                                        lockData:
                                                                          file.locked_linked_items,
                                                                        chapterTitle:
                                                                          file.name
                                                                      }
                                                                    );
                                                                  }
                                                                  if (
                                                                    file.locked_until_date &&
                                                                    file.lock_type_id ===
                                                                      2
                                                                  ) {
                                                                    setLockItems(
                                                                      {
                                                                        lockDate:
                                                                          file.locked_until_date,
                                                                        chapterTitle:
                                                                          file.name
                                                                      }
                                                                    );
                                                                  }
                                                                }}>
                                                                <LockIcon />
                                                              </IconButton>
                                                            )
                                                          ) : (
                                                            file.lock_type_id !==
                                                              1 && (
                                                              <IconButton
                                                                size="small"
                                                                onClick={event => {
                                                                  setLockItemsOpen(
                                                                    true
                                                                  );
                                                                  if (
                                                                    file.locked_linked_items &&
                                                                    file.lock_type_id ===
                                                                      3
                                                                  ) {
                                                                    setLockItems(
                                                                      {
                                                                        lockData:
                                                                          file.locked_linked_items,
                                                                        chapterTitle:
                                                                          file.name
                                                                      }
                                                                    );
                                                                  }
                                                                  if (
                                                                    file.locked_until_date &&
                                                                    file.lock_type_id ===
                                                                      2
                                                                  ) {
                                                                    setLockItems(
                                                                      {
                                                                        lockDate:
                                                                          file.locked_until_date,
                                                                        chapterTitle:
                                                                          file.name
                                                                      }
                                                                    );
                                                                  }
                                                                }}>
                                                                <LockIcon />
                                                              </IconButton>
                                                            )
                                                          )}
                                                          {/* <Typography
                                                            variant="caption"
                                                            sx={{mr: 2}}>
                                                            {file?.score}
                                                          </Typography> */}
                                                          {lmstype === 'view' &&
                                                            lmsisAdmin ===
                                                              'False' && (
                                                              <IconButton
                                                                onClick={() =>
                                                                  handleCompleted(
                                                                    file.id,
                                                                    module.id,
                                                                    file.type,
                                                                    file?.ChildItems_Completed
                                                                  )
                                                                }
                                                                disabled={
                                                                  file.type ===
                                                                    'onlinetest' ||
                                                                  (file.lock_type_id ===
                                                                    2 &&
                                                                    !file?.IsUnlocked) ||
                                                                  (file.lock_type_id ===
                                                                    3 &&
                                                                    !file?.is_completed)
                                                                    ? true
                                                                    : file?.ChildItems_Completed
                                                                }
                                                                sx={{
                                                                  width: 25,
                                                                  height: 25,
                                                                  borderRadius:
                                                                    '50%',
                                                                  marginRight: 1,
                                                                  backgroundColor:
                                                                    file.ChildItems_Completed
                                                                      ? 'green'
                                                                      : '#b3beb3',
                                                                  border:
                                                                    file.ChildItems_Completed
                                                                      ? 'none'
                                                                      : '2px solid gray',
                                                                  color:
                                                                    file.ChildItems_Completed
                                                                      ? 'white'
                                                                      : 'gray',
                                                                  transition:
                                                                    'all 0.3s',
                                                                  '&:hover': {
                                                                    backgroundColor:
                                                                      file.ChildItems_Completed
                                                                        ? 'darkgreen'
                                                                        : 'rgba(0, 0, 0, 0.1)'
                                                                  },
                                                                  '&.Mui-disabled':
                                                                    {
                                                                      backgroundColor:
                                                                        file.ChildItems_Completed
                                                                          ? 'green'
                                                                          : 'transparent',
                                                                      color:
                                                                        file.ChildItems_Completed
                                                                          ? 'white'
                                                                          : 'rgba(145, 158, 171, 0.8)'
                                                                    }
                                                                }}>
                                                                {<CheckIcon />}
                                                              </IconButton>
                                                            )}
                                                          {lmstype !==
                                                            'view' && (
                                                            <IconButton
                                                              sx={{mr: 2}}
                                                              size="small"
                                                              onClick={event => {
                                                               
                                                              
                                                                return handleFileMenuOpen(
                                                                  event,
                                                                  file.id,
                                                                  module.id,
                                                                  file.type,
                                                                  file
                                                                );
                                                              }}>
                                                              <MoreHoriz
                                                                className="mhoriz-indicator"
                                                                sx={{
                                                                  fill: '#F4F6F8', // Default color
                                                                  transition:
                                                                    'fill 0.3s ease-in-out'
                                                                }}
                                                              />
                                                            </IconButton>
                                                          )}
                                                        </Box>
                                                      </Box>
                                                      {file?.additionalFiles &&
                                                        file.additionalFiles.map(
                                                          (
                                                            additional,
                                                            index
                                                          ) => (
                                                            <Box
                                                              key={`${additional.id}_${additional.name}`}
                                                              sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                  'center',
                                                                width: '100%',
                                                                padding: '8px',
                                                                pl: '6vh'
                                                              }}>
                                                              <Typography fontSize="0.9rem">
                                                                {
                                                                  additional.name
                                                                }
                                                              </Typography>
                                                            </Box>
                                                          )
                                                        )}
                                                      {/* Dropdown Menu */}
                                                      <Menu
                                                        anchorEl={
                                                          menuAnchorfile
                                                        }
                                                        open={
                                                          Boolean(
                                                            menuAnchorfile
                                                          ) &&
                                                          selectedFile?.fileId ===
                                                            file.id
                                                        }
                                                        onClose={
                                                          handleMenuClose
                                                        }>
                                                        <MenuItem
                                                          onClick={e => {
                                                            
                                                            if (!selectedFile)
                                                              return;
                                                            const {
                                                              fileId,
                                                              moduleID,
                                                              operation,
                                                              module_id,
                                                              type,
                                                              data
                                                            } = selectedFile;
                                                            if (
                                                              type === 'file'
                                                            ) {
                                                              addingFile.onTrue();
                                                            }
                                                            if (
                                                              type === 'link'
                                                            ) {
                                                              addingLinks.onTrue();
                                                            }
                                                            if (
                                                              type === 'content'
                                                            ) {
                                                              addingContents.onTrue();
                                                            }
                                                            if (
                                                              type ===
                                                              'onlinetest'
                                                            ) {
                                                              addingOnlineTest.onTrue();
                                                            }
                                                            if (
                                                              type ===
                                                              'assignment'
                                                            ) {
                                                              addingAssignments.onTrue();
                                                            }
                                                            if (
                                                              fileId &&
                                                              moduleID
                                                            ) {
                                                              handleSelectionFile(
                                                                e,
                                                                fileId,
                                                                moduleID,
                                                                'UPDATE',
                                                                type,
                                                                data
                                                              );
                                                            }
                                                          }}>
                                                          Edit
                                                        </MenuItem>
                                                        <MenuItem
                                                          onClick={e => {
                                                            confirm.onTrue();
                                                            if (!selectedFile)
                                                              return;
                                                            const {
                                                              fileId,
                                                              moduleID,
                                                              operation,
                                                              module_id,
                                                              type,
                                                              data
                                                            } = selectedFile;
                                                            if (
                                                              fileId &&
                                                              moduleID
                                                            ) {
                                                              handleSelectionFile(
                                                                e,
                                                                fileId,
                                                                moduleID,
                                                                'DELETE',
                                                                type
                                                              );
                                                            }
                                                          }}>
                                                          Delete
                                                        </MenuItem>
                                                      </Menu>
                                                    </Paper>
                                                  )}
                                                </Draggable>
                                              ))}
                                          {dropProvided.placeholder}
                                        </Stack>
                                      )}
                                    </Droppable>
                                  </Collapse>
                                </div>
                                {dropProvided.placeholder}
                              </Stack>
                            )}
                          </Droppable>
                        </Paper>
                      )}
                    </Draggable>
                  )
                )}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <ModuleDetails
        item={moduleDetails}
        open={openDetails.value}
        onClose={openDetails.onFalse}
      />
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <LoadingButton
            variant="contained"
            color="error"
            loading={deleteLoading}
            onClick={async () => {
              await onDelete();
            }}>
            Delete
          </LoadingButton>
        }
      />
      {addingFile.value &&
        (selectedFile?.operation || selectedAddModuleId?.operation) && (
          <AddFileSModule
            task={selectedFile}
            uiFieldsdata={
              uiFieldsvals?.file_json ? uiFieldsvals?.file_json : undefined
            }
            openDetails={addingFile.value}
            taskDone={selectedAddModuleId}
            onUpdateTask={e => {
              console.log('eeee', e);
            }}
            onCloseDetails={() => {
              addingFile.onFalse();
              setSelectedFile(undefined);
              setSelectedAddModuleid(undefined);
              // init();
            }}
          />
        )}
      {addingLinks.value &&
        (selectedFile?.operation || selectedAddModuleId?.operation) && (
          <AddLinksModule
            task={selectedFile}
            uiFieldsdata={
              uiFieldsvals?.link_json ? uiFieldsvals?.link_json : undefined
            }
            openDetails={addingLinks.value}
            taskDone={selectedAddModuleId}
            onUpdateTask={e => {
              console.log('eeee', e);
            }}
            onCloseDetails={() => {
              addingLinks.onFalse();
              setSelectedFile(undefined);
              setSelectedAddModuleid(undefined);
              // init();
            }}
          />
        )}
      {addingAssignments.value &&
        (selectedFile?.operation || selectedAddModuleId?.operation) && (
          <AddAssignments
            task={selectedFile}
            uiFieldsdata={
              uiFieldsvals?.assignment_json
                ? uiFieldsvals?.assignment_json
                : undefined
            }
            openDetails={addingAssignments.value}
            taskDone={selectedAddModuleId}
            onUpdateTask={e => {
              console.log('eeee', e);
            }}
            onCloseDetails={() => {
              addingAssignments.onFalse();
              setSelectedFile(undefined);
              setSelectedAddModuleid(undefined);
              // init();
            }}
          />
        )}
      {addingContents.value &&
        (selectedFile?.operation || selectedAddModuleId?.operation) && (
          <AddContent
            task={selectedFile}
            uiFieldsdata={
              uiFieldsvals?.content_json
                ? uiFieldsvals?.content_json
                : undefined
            }
            openDetails={addingContents.value}
            taskDone={selectedAddModuleId}
            onUpdateTask={e => {
              console.log('eeee', e);
            }}
            onCloseDetails={() => {
              addingContents.onFalse();
              setSelectedFile(undefined);
              setSelectedAddModuleid(undefined);
              // init();
            }}
          />
        )}
      {addingOflineTest.value &&
        (selectedFile?.operation || selectedAddModuleId?.operation) && (
          <AddOfflineTest
            task={offlineTestAddded}
            uiFieldsdata={
              uiFieldsvals?.offlinetest_json
                ? uiFieldsvals?.offlinetest_json
                : undefined
            }
            taskDone={selectedAddModuleId}
            openDetails={addingOflineTest.value}
            onUpdateTask={e => {
              console.log('eeee', e);
            }}
            onCloseDetails={() => {
              addingOflineTest.onFalse();
              setSelectedFile(undefined);
              setSelectedAddModuleid(undefined);
              // init();
            }}
          />
        )}
      {addingOnlineTest.value &&
        (selectedFile?.operation || selectedAddModuleId?.operation) && (
          <OnlineContextProvider>
            <AddOnlineTest
              task={selectedFile}
              uiFieldsdata={
                uiFieldsvals?.onlinetest_json
                  ? uiFieldsvals?.onlinetest_json
                  : undefined
              }
              taskDone={selectedAddModuleId}
              openDetails={addingOnlineTest.value}
              onUpdateTask={e => {
                console.log('eeee', e);
              }}
              onCloseDetails={() => {
                addingOnlineTest.onFalse();
                setSelectedFile(undefined);
                setSelectedAddModuleid(undefined);
                // init();
              }}
            />
          </OnlineContextProvider>
        )}
      {editmodule && (
        <CreateNewModule
          open={editmodule}
          uiFieldsdata={
            uiFieldsvals?.create_module_json
              ? uiFieldsvals?.create_module_json
              : undefined
          }
          passedData={selectedModule}
          onClose={() => {
            setEditModule(false);
            setSelectedModule(undefined);
            // init();
          }}
          title="Edit Module"
          operation="UPDATE"
        />
      )}
      {isLockItemsOpen && (
        <LockedDialog
          open={isLockItemsOpen}
          onClose={() => {
            setLockItemsOpen(false);
            setLockItems({lockData: [], chapterTitle: ''});
          }}
          items={lockitems}
        />
      )}
      {isunLockItemsOpen && (
        <UnLockedDialog
          open={isunLockItemsOpen}
          onClose={() => {
            setunLockItemsOpen(false);
            setunLockItems({lockData: [], chapterTitle: ''});
          }}
          items={unlockitems}
        />
      )}
    </>
  ) : lmsisAdmin === 'True' ? (
    <Container
      sx={{
        display: 'flex',
        marginTop: '2vh',
        justifyContent: 'center',
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '100% !important',
        height: '71vh'
      }}>
      <div>
        <div>
          <Stack
            spacing={3}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap">
            <Stack spacing={1} sx={{textAlign: 'center'}}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '55vh',
                  fontSize: '20px !important'
                }}>
                To get started, create a
                <Box
                  component="span"
                  onClick={() => handleAddNewRecord('Add module', 'module')}
                  sx={{
                    mx: 0.5,
                    color: 'primary.main',
                    cursor: 'pointer'
                  }}>
                  new module
                </Box>
                {/* {or
                <Box
                  component="span"
                  onClick={() => handleAddNewRecord('Import', 'module')}
                  sx={{
                    mx: 0.5,
                    color: 'primary.main',
                    cursor: 'pointer'
                  }}>
                  import ModulePreviewData
                </Box>
                from another course for a quick start */}
              </Typography>
            </Stack>
          </Stack>
        </div>
        {/* Add your form elements here */}
      </div>
    </Container>
  ) : (
    <Container
      sx={{
        display: 'flex',
        marginTop: '2vh',
        justifyContent: 'center',
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '100% !important',
        height: '71vh'
      }}>
      <div>
        <div>
          <Stack
            spacing={3}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap">
            <Stack spacing={1} sx={{textAlign: 'center'}}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '55vh',
                  fontSize: '20px !important'
                }}>
                No Modules Created
                {/* {or
                <Box
                  component="span"
                  onClick={() => handleAddNewRecord('Import', 'module')}
                  sx={{
                    mx: 0.5,
                    color: 'primary.main',
                    cursor: 'pointer'
                  }}>
                  import ModulePreviewData
                </Box>
                from another course for a quick start */}
              </Typography>
            </Stack>
          </Stack>
        </div>
        {/* Add your form elements here */}
      </div>
    </Container>
  );
};

export default ModuleMainPage;
